import { Badge, Button, CloseButton, Group, Select, Switch, TextInput } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import {
  IconCalendarEvent,
  IconChecklist,
  IconHome,
  IconMessage,
  IconPlus,
  IconReceipt,
  IconTools,
} from '@tabler/icons-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import UserAvatar from 'src/Components/NavBar/UserAvatar';
import { RichTextEditor } from 'src/Components/Shared/RichTextEditor';
import { SlateNode } from 'src/Components/Shared/SlateRenderer/SlateRenderer.types';
import useFetchAllEvents from 'src/Hooks/Queries/useFetchAllEvents';
import useFetchChecklists from 'src/Hooks/Queries/useFetchChecklists';
import useFetchExpenses from 'src/Hooks/Queries/useFetchExpenses';
import useFetchMembers from 'src/Hooks/Queries/useFetchMembers';
import useFetchProjects from 'src/Hooks/Queries/useFetchProjects';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import {
  RelatedEntityInput,
  RelatedEntityType,
  SUBCATEGORY_TO_CATEGORY,
  TaskCategory,
  TaskStatus,
  TaskSubCategory,
} from 'src/types/task.types';

import styles from './TaskForm.module.scss';
import { Props } from './TaskForm.types';

const CONTEXT_ICON: Record<RelatedEntityType, React.ReactNode> = {
  project: <IconTools size={14} />,
  event: <IconCalendarEvent size={14} />,
  expense: <IconReceipt size={14} />,
  checklist: <IconChecklist size={14} />,
  communityFeedItem: <IconMessage size={14} />,
};

const TaskForm = ({
  form,
  onSubmit,
  onCancel,
  onDelete,
  isLoading,
  isDeleting,
  isEditing,
}: Props) => {
  const { t } = useTranslation();
  const assetId = useCurrentAssetId();
  const { data: membersData, isLoading: isLoadingMembers } = useFetchMembers();
  const { data: projects, isLoading: isLoadingProjects } = useFetchProjects(assetId ?? '');
  const { data: events, isLoading: isLoadingEvents } = useFetchAllEvents();
  const { data: expenses, isLoading: isLoadingExpenses } = useFetchExpenses();
  const { data: checklists, isLoading: isLoadingChecklists } = useFetchChecklists();

  // Local state for the "add entity" row
  const [addingType, setAddingType] = useState<RelatedEntityType | null>(null);
  const [addingEntityId, setAddingEntityId] = useState<string | null>(null);

  const memberOptions = useMemo(() => {
    if (!membersData) return [];
    return membersData.members.map(member => ({
      value: member.user.id,
      label: `${member.user.firstName} ${member.user.lastName}`,
      user: member.user,
    }));
  }, [membersData]);

  const contextTypeOptions = [
    { value: 'project', label: t('tasks.context.project', 'Idées & Projets › Projet') },
    { value: 'event', label: t('tasks.context.event', 'Planning › Événement') },
    { value: 'expense', label: t('tasks.context.expense', 'Budget › Dépense') },
    { value: 'checklist', label: t('tasks.context.checklist', 'Checklists › Checklist') },
  ];

  const projectOptions = useMemo(() => {
    if (!projects) return [];
    return projects.map(project => ({ value: project.id, label: project.title }));
  }, [projects]);

  const eventOptions = useMemo(() => {
    if (!events) return [];
    return events.map(event => ({ value: event.id, label: event.name }));
  }, [events]);

  const expenseOptions = useMemo(() => {
    if (!expenses) return [];
    return expenses.map(expense => ({ value: expense.id, label: expense.title }));
  }, [expenses]);

  const checklistOptions = useMemo(() => {
    if (!checklists) return [];
    return checklists.map(checklist => ({ value: checklist.id, label: checklist.title }));
  }, [checklists]);

  const getEntityOptions = (type: RelatedEntityType) => {
    switch (type) {
      case 'project':
        return { options: projectOptions, loading: isLoadingProjects };
      case 'event':
        return { options: eventOptions, loading: isLoadingEvents };
      case 'expense':
        return { options: expenseOptions, loading: isLoadingExpenses };
      case 'checklist':
        return { options: checklistOptions, loading: isLoadingChecklists };
      case 'communityFeedItem':
        return { options: [], loading: false };
    }
  };

  const getEntityLabel = useCallback(
    (type: RelatedEntityType, id: string): string => {
      switch (type) {
        case 'project':
          return projects?.find(p => p.id === id)?.title ?? id;
        case 'event':
          return events?.find(e => e.id === id)?.name ?? id;
        case 'expense':
          return expenses?.find(e => e.id === id)?.title ?? id;
        case 'checklist':
          return checklists?.find(c => c.id === id)?.title ?? id;
        case 'communityFeedItem':
          return t('tasks.form.linkedToPost', 'Ma Communauté › Publication');
      }
    },
    [projects, events, expenses, checklists, t]
  );

  const handleAddEntity = () => {
    if (!addingType) return;
    if (addingType !== 'communityFeedItem' && !addingEntityId) return;

    const currentEntities = form.values.relatedEntities;
    const entityId = addingEntityId ?? '';
    const alreadyExists = currentEntities.some(e => e.type === addingType && e.id === entityId);
    if (alreadyExists) return;

    form.setFieldValue('relatedEntities', [...currentEntities, { type: addingType, id: entityId }]);
    setAddingType(null);
    setAddingEntityId(null);
  };

  const handleRemoveEntity = (index: number) => {
    const currentEntities = [...form.values.relatedEntities];
    currentEntities.splice(index, 1);
    form.setFieldValue('relatedEntities', currentEntities);
  };

  const handleAddTypeChange = (value: string | null) => {
    setAddingType(value as RelatedEntityType | null);
    setAddingEntityId(null);
  };

  const categoryOptions = [
    { value: TaskCategory.TECHNIQUE, label: t('tasks.category.technique', 'Technique') },
    { value: TaskCategory.VIE_MAISON, label: t('tasks.category.vieMaison', 'Vie de la maison') },
    { value: TaskCategory.GESTION, label: t('tasks.category.gestion', 'Gestion') },
  ];

  const subCategoryOptions: Record<TaskCategory, { value: TaskSubCategory; label: string }[]> = {
    [TaskCategory.TECHNIQUE]: [
      { value: TaskSubCategory.PLOMBERIE, label: t('tasks.subCategory.plomberie', 'Plomberie') },
      {
        value: TaskSubCategory.ELECTRICITE,
        label: t('tasks.subCategory.electricite', 'Électricité'),
      },
      {
        value: TaskSubCategory.MENUISERIES,
        label: t('tasks.subCategory.menuiseries', 'Menuiseries'),
      },
      {
        value: TaskSubCategory.PEINTURE_ENDUITS,
        label: t('tasks.subCategory.peintureEnduits', 'Peinture & Enduits'),
      },
      {
        value: TaskSubCategory.REPARATIONS_DIVERSES,
        label: t('tasks.subCategory.reparationsDiverses', 'Réparations diverses'),
      },
      { value: TaskSubCategory.JARDINAGE, label: t('tasks.subCategory.jardinage', 'Jardinage') },
    ],
    [TaskCategory.VIE_MAISON]: [
      {
        value: TaskSubCategory.MENAGE_RANGEMENT,
        label: t('tasks.subCategory.menageRangement', 'Ménage & Rangement'),
      },
      {
        value: TaskSubCategory.DECORATION,
        label: t('tasks.subCategory.decoration', 'Décoration'),
      },
      {
        value: TaskSubCategory.COURSES_APPROVISIONNEMENT,
        label: t('tasks.subCategory.coursesApprovisionnement', 'Courses & Approvisionnement'),
      },
    ],
    [TaskCategory.GESTION]: [
      {
        value: TaskSubCategory.ADMINISTRATIF,
        label: t('tasks.subCategory.administratif', 'Administratif'),
      },
      {
        value: TaskSubCategory.COMPTABILITE,
        label: t('tasks.subCategory.comptabilite', 'Comptabilité'),
      },
      { value: TaskSubCategory.JURIDIQUE, label: t('tasks.subCategory.juridique', 'Juridique') },
      {
        value: TaskSubCategory.SOURCING_ACHATS,
        label: t('tasks.subCategory.sourcingAchats', 'Sourcing & Achats'),
      },
      {
        value: TaskSubCategory.GESTION_SINISTRES,
        label: t('tasks.subCategory.gestionSinistres', 'Gestion des sinistres'),
      },
      {
        value: TaskSubCategory.RELATIONS_EXTERIEURES,
        label: t('tasks.subCategory.relationsExterieures', 'Relations extérieures'),
      },
    ],
  };

  const statusOptions = [
    { value: TaskStatus.TODO, label: t('tasks.status.todo', 'À faire') },
    { value: TaskStatus.IN_PROGRESS, label: t('tasks.status.inProgress', 'En cours') },
    { value: TaskStatus.DONE, label: t('tasks.status.done', 'Terminé') },
  ];

  const currentCategory = form.values.category;
  const availableSubCategories = currentCategory ? subCategoryOptions[currentCategory] : [];

  const handleCategoryChange = (value: string | null) => {
    form.setFieldValue('category', value as TaskCategory | null);
    const currentSubCategory = form.values.subCategory;
    if (currentSubCategory && value) {
      const expectedCategory = SUBCATEGORY_TO_CATEGORY[currentSubCategory];
      if (expectedCategory !== value) {
        form.setFieldValue('subCategory', null);
      }
    } else if (!value) {
      form.setFieldValue('subCategory', null);
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (addingType && addingEntityId) {
      const currentEntities = form.values.relatedEntities;
      const alreadyExists = currentEntities.some(
        entity => entity.type === addingType && entity.id === addingEntityId
      );
      if (!alreadyExists) {
        form.setFieldValue('relatedEntities', [
          ...currentEntities,
          { type: addingType, id: addingEntityId },
        ]);
      }
    }
    onSubmit(e);
  };

  const entitySelector = addingType && addingType !== 'communityFeedItem' && (
    <Select
      placeholder={t('tasks.form.selectEntity', 'Sélectionner...')}
      data={getEntityOptions(addingType).options}
      clearable
      searchable
      disabled={getEntityOptions(addingType).loading}
      style={{ flex: 1 }}
      value={addingEntityId}
      onChange={setAddingEntityId}
    />
  );

  return (
    <form onSubmit={handleSubmit}>
      <div className={styles.formStack}>
        <TextInput
          label={t('common.title', 'Titre')}
          placeholder={t('tasks.form.titlePlaceholder', 'Ex: Réparer le robinet')}
          required
          {...form.getInputProps('title')}
        />

        <div>
          <div className={styles.label}>{t('common.description', 'Description')}</div>
          <RichTextEditor
            value={form.values.description}
            onChange={value => form.setFieldValue('description', value as SlateNode[])}
            placeholder={t('tasks.form.descriptionPlaceholder', 'Détails de la tâche...')}
          />
        </div>

        <Select
          label={t('common.status', 'Statut')}
          data={statusOptions}
          {...form.getInputProps('status')}
        />

        <div className={styles.row}>
          <Select
            label={t('tasks.form.category', 'Catégorie')}
            placeholder={t('tasks.form.categoryPlaceholder', 'Sélectionner une catégorie')}
            data={categoryOptions}
            clearable
            style={{ flex: 1 }}
            value={form.values.category}
            onChange={handleCategoryChange}
          />

          <Select
            label={t('tasks.form.subCategory', 'Sous-catégorie')}
            placeholder={t('tasks.form.subCategoryPlaceholder', 'Sélectionner')}
            data={availableSubCategories}
            clearable
            disabled={!currentCategory}
            style={{ flex: 1 }}
            {...form.getInputProps('subCategory')}
          />
        </div>

        <DatePickerInput
          label={t('tasks.form.dueDate', 'Date limite')}
          placeholder={t('tasks.form.dueDatePlaceholder', 'Sélectionner une date')}
          clearable
          {...form.getInputProps('dueDate')}
        />

        <Switch
          className={styles.onSiteSwitch}
          label={
            <Group gap={6}>
              <IconHome size={16} />
              <span>{t('tasks.form.requiresOnSite', 'À faire sur place')}</span>
            </Group>
          }
          description={t(
            'tasks.form.requiresOnSiteDescription',
            'Cocher si cette tâche nécessite une présence physique à la maison'
          )}
          {...form.getInputProps('requiresOnSite', { type: 'checkbox' })}
        />

        <Select
          label={t('tasks.form.assignedTo', 'Assigné à')}
          placeholder={t('tasks.form.assignedToPlaceholder', 'Sélectionner un membre')}
          data={memberOptions}
          clearable
          searchable
          disabled={isLoadingMembers}
          renderOption={({ option }) => {
            const member = memberOptions.find(m => m.value === option.value);
            return (
              <Group gap="sm">
                <UserAvatar user={member?.user} size="sm" />
                <span>{option.label}</span>
              </Group>
            );
          }}
          {...form.getInputProps('assignedToId')}
        />

        <div className={styles.contextSection}>
          <div className={styles.label}>{t('tasks.form.linkedEntities', 'Éléments liés')}</div>

          {/* List of currently linked entities */}
          {form.values.relatedEntities.length > 0 && (
            <Group gap="xs" mb="sm">
              {form.values.relatedEntities.map((entity: RelatedEntityInput, index: number) => (
                <Badge
                  key={`${entity.type}-${entity.id}`}
                  variant="light"
                  color="violet"
                  leftSection={CONTEXT_ICON[entity.type]}
                  rightSection={
                    <CloseButton
                      size="xs"
                      variant="transparent"
                      onClick={() => handleRemoveEntity(index)}
                    />
                  }
                >
                  {getEntityLabel(entity.type, entity.id)}
                </Badge>
              ))}
            </Group>
          )}

          {/* Add entity row */}
          <div className={styles.addEntityRow}>
            <Select
              placeholder={t('tasks.form.contextTypePlaceholder', 'Type')}
              data={contextTypeOptions}
              clearable
              style={{ flex: 1 }}
              value={addingType}
              onChange={handleAddTypeChange}
            />
            {entitySelector}
            <Button
              variant="light"
              leftSection={<IconPlus size={16} />}
              disabled={!addingType || (addingType !== 'communityFeedItem' && !addingEntityId)}
              onClick={handleAddEntity}
            >
              {t('common.add', 'Ajouter')}
            </Button>
          </div>
        </div>

        <div className={styles.buttonGroup}>
          {isEditing && onDelete && (
            <Button variant="danger" onClick={onDelete} loading={isDeleting}>
              {t('common.delete', 'Supprimer')}
            </Button>
          )}
          <div className={styles.actions}>
            <Button variant="light" onClick={onCancel}>
              {t('common.cancel', 'Annuler')}
            </Button>
            <Button type="submit" loading={isLoading}>
              {isEditing ? t('common.save', 'Enregistrer') : t('common.create', 'Créer')}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default TaskForm;
